import { isDefined, isNotDefined } from '@sgme/fp';
import type { QuoteInfo } from 'apiConnector/scheduledRun/model';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import { batchFormReloadRequested } from 'state/batchForm/batchForm.action';
import {
  getEditableScheduledRun,
  getEditableScheduledRunId,
} from 'state/blotters/scheduledQuotes/scheduledQuotesBlotter.selector';
import { scheduledRunActions } from 'state/scheduledRuns/actions';
import { OCCURRENCE_RELOAD_OUTCOME, validateOccurrenceBeforeReload } from 'state/scheduledRuns/occurrenceReload';
import type { Thunk } from 'state/state.common';
import { batchWorkspaceProductSelectedThunk } from 'state/workspaces/batch/tabContent/tabContent.thunks';
import { workspaceTabActivated, workspaceTabCreated } from 'state/workspaces/workspace.actions';
import { asSuccess } from 'utils/status';
import { getToastUtils } from 'utils/toast';

export function addToScheduleThunk(id: string, quotes: QuoteInfo[]): Thunk<void> {
  return dispatch => {
    dispatch(scheduledRunActions.updateQuotes(id, quotes));
    dispatch(scheduledRunActions.scheduledRunRunsRequested(id));
  };
}

export function openScheduledRunOccurrencesThunk(id: string): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();
    const scheduledRun = getEditableScheduledRun(state);
    if (scheduledRun.occurrences.status === 'loading' || scheduledRun.occurrenceReload.status === 'loading') {
      return;
    }
    const runId = getEditableScheduledRunId(state);
    if (isDefined(runId)) {
      dispatch(scheduledRunActions.occurrencesRequested(id, runId, getNewGuid()));
    }
  };
}

export function requestScheduledRunOccurrenceDetailThunk(id: string): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();
    const scheduledRun = getEditableScheduledRun(state);
    if (scheduledRun.occurrenceDetail.status === 'loading' || scheduledRun.occurrenceReload.status !== 'idle') {
      return;
    }
    const selectedRun = asSuccess(scheduledRun.runs)?.content.find(run => run.isSelected);
    const occurrenceId = scheduledRun.selectedOccurrenceId;
    if (isNotDefined(selectedRun) || isNotDefined(occurrenceId)) {
      return;
    }
    dispatch(scheduledRunActions.occurrenceDetailRequested(id, selectedRun.id, occurrenceId, getNewGuid()));
  };
}

export function reloadScheduledRunOccurrenceThunk(id: string, detailRequestId?: string): Thunk<void> {
  return (dispatch, getState, { getNewGuid, getSgmeConfiguration, getTodayString, navigateTo }) => {
    const state = getState();
    const scheduledRun = getEditableScheduledRun(state);
    if (scheduledRun.occurrenceReload.status !== 'idle') {
      return;
    }
    if (isDefined(detailRequestId) && scheduledRun.occurrenceDetailRequestId !== detailRequestId) {
      return;
    }
    const selectedRun = asSuccess(scheduledRun.runs)?.content.find(run => run.isSelected);
    const selectedOccurrence = asSuccess(scheduledRun.occurrenceDetail)?.content;
    const toast = getToastUtils(dispatch);
    if (
      isNotDefined(selectedRun) ||
      isNotDefined(selectedOccurrence) ||
      selectedOccurrence.occurrenceId !== scheduledRun.selectedOccurrenceId ||
      (isDefined(selectedOccurrence.runId) && selectedOccurrence.runId !== selectedRun.id)
    ) {
      return;
    }

    const { outcome, groups, skippedCount } = validateOccurrenceBeforeReload(
      selectedRun.quotes,
      selectedOccurrence.quotes,
    );
    if (outcome === OCCURRENCE_RELOAD_OUTCOME.EMPTY) {
      toast.info({ id: 'scheduledRun.occurrenceReload.empty' }, { id: 'scheduledRun.occurrenceReload.title' });
      return;
    }

    if (outcome === OCCURRENCE_RELOAD_OUTCOME.INVALID) {
      toast.error(
        { id: 'scheduledRun.occurrenceReload.missingMetadata' },
        { id: 'scheduledRun.occurrenceReload.title' },
        false,
      );
      return;
    }

    if (isNotDefined(groups) || groups.length === 0 || isNotDefined(skippedCount)) {
      toast.error(
        { id: 'scheduledRun.occurrenceReload.missingMetadata' },
        { id: 'scheduledRun.occurrenceReload.title' },
        false,
      );
      return;
    }

    const configuredMaxForms = getSgmeConfiguration().maxNumberOfFormsPerBatchTab;
    const maxForms = Number.isFinite(configuredMaxForms) ? Math.max(0, Math.floor(configuredMaxForms)) : 0;
    const groupPlans = groups.map(group => ({
      ...group,
      acceptedQuotes: group.orderedQuotes.slice(0, maxForms),
      skippedQuoteIds: group.orderedQuotes.slice(maxForms).map(quote => quote.quoteId),
      tabId: getNewGuid(),
    }));
    if (groupPlans.every(group => group.acceptedQuotes.length === 0)) {
      toast.info({ id: 'scheduledRun.occurrenceReload.empty' }, { id: 'scheduledRun.occurrenceReload.title' });
      return;
    }

    const operationId = getNewGuid();
    const tabName = `${selectedRun.name} - Reload`;

    dispatch(
      scheduledRunActions.occurrenceReloadStarted(
        id,
        selectedRun.id,
        operationId,
        groupPlans.map(group => group.tabId),
        groupPlans.flatMap(group => group.acceptedQuotes.map(quote => quote.quoteId)),
        groupPlans.flatMap(group => group.skippedQuoteIds),
        skippedCount,
        Object.fromEntries(groupPlans.map(group => [group.tabId, group.acceptedQuotes.map(quote => quote.quoteId)])),
      ),
    );
    groupPlans.forEach(group => {
      const firstQuote = group.acceptedQuotes[0];
      if (isNotDefined(firstQuote)) {
        return;
      }
      dispatch(workspaceTabCreated(group.tabId, 'newBatch', 'reprice', tabName));
      group.acceptedQuotes.forEach(quote => {
        dispatch(
          batchFormReloadRequested(
            group.tabId,
            getNewGuid(),
            quote.quoteId,
            IdType.ExternalQuoteId,
            getTodayString(),
            quote.productType,
            quote.productSubType,
            quote.wrapper,
            { operationId, quoteId: quote.quoteId, scheduledRunId: id },
          ),
        );
      });
      dispatch(
        batchWorkspaceProductSelectedThunk(
          group.tabId,
          firstQuote.productType,
          firstQuote.productSubType,
          firstQuote.wrapper,
          undefined,
          tabName,
          true,
        ),
      );
    });
    const firstTabId = groupPlans[0]?.tabId;
    if (isDefined(firstTabId)) {
      dispatch(workspaceTabActivated(firstTabId, 'newBatch', 'reprice'));
    }
    navigateTo('/batch');
  };
}
